// Shared color-space utilities for consistent rendering between scene and palette.

import { clamp01, hexNumberToRgb } from '@/utils/color'

// Build once: sRGB (0..255) -> linear (0..255) using IEC 61966-2-1 EOTF
export const SRGB_TO_LINEAR_BYTE: Uint8Array = (() => {
  const lut = new Uint8Array(256)
  for (let i = 0; i < 256; i++) {
    const srgb = i / 255
    const linear = srgb <= 0.04045 ? srgb / 12.92 : Math.pow((srgb + 0.055) / 1.055, 2.4)
    lut[i] = Math.round(clamp01(linear) * 255)
  }
  return lut
})()

export function srgb8ToLinearFloat(v: number): number {
  const c = v / 255
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

export function linearFloatToSrgb8(v: number): number {
  const c = clamp01(v)
  const srgb = c <= 0.0031308 ? c * 12.92 : 1.055 * Math.pow(c, 1 / 2.4) - 0.055
  return Math.round(clamp01(srgb) * 255)
}

export function applyExposureToSrgbBytes(r: number, g: number, b: number, exposure: number): [number, number, number] {
  const lr = srgb8ToLinearFloat(r) * exposure
  const lg = srgb8ToLinearFloat(g) * exposure
  const lb = srgb8ToLinearFloat(b) * exposure
  return [linearFloatToSrgb8(lr), linearFloatToSrgb8(lg), linearFloatToSrgb8(lb)]
}

// Convert a hex sRGB color (0xRRGGBB) to linear 8-bit tuple [r,g,b]
export function hexToLinearBytes(hex: number): [number, number, number] {
  const { r, g, b } = hexNumberToRgb(hex)
  return [SRGB_TO_LINEAR_BYTE[r], SRGB_TO_LINEAR_BYTE[g], SRGB_TO_LINEAR_BYTE[b]]
}
